import React, { Suspense, useEffect } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { loginRequest, scopes } from "@/config/authConfig";
import { Loader } from "@/components/Elements/Loader";
import { removeSessionStorage } from "@/lib/utils/queryHelpers";
import { useGetCurrentAccount } from "@/features/account/api/getCurrentAccount";

type Props = {
  /**
   * Content to be displayed within the wrapper after authentication
   */
  children: React.ReactNode;
};

export const AuthenticationWrapper = ({ children }: Props) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { data: account, isLoading: isAccountLoading } = useGetCurrentAccount({
    enabled: !isAuthenticated,
  });

  useEffect(() => {
    const account = accounts[0];
    // If user is already logged in or MSAL is still performing an in-progress login, do nothing
    if (inProgress !== InteractionStatus.None || isAuthenticated) {
      return;
    }
    // If there are accounts, fetch the token silently without prompting the user,
    // otherwise redirect to login page
    if (account) {
      const accessTokenRequest = {
        scopes: scopes,
        account: accounts[0],
      };

      instance.acquireTokenSilent(accessTokenRequest).catch((error) => {
        // Call acquireToken with redirect in case of acquireTokenSilent failure
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest).catch((error) => {
            console.log(error);
          });
        }
      });
      return;
    }

    // If there is no account, redirect to login
    // Introduce a small delay to prevent infinite redirects on page load
    setTimeout(() => {
      removeSessionStorage("currentSpaceId");
      instance
        .loginRedirect(loginRequest)
        .then(() => {})
        .catch((error) => {
          console.log(error);
        });
    }, 1000);
  }, [instance, isAuthenticated, accounts, inProgress]);

  useEffect(() => {
    if (account && account.forceLogOut) {
      // If the user is logged out, remove the session storage and redirect to login
      removeSessionStorage("currentSpaceId");
      instance.logoutRedirect().catch((error) => {
        console.log(error);
      });
    }
  }, [account]);

  return (
    <Suspense
      fallback={
        <Loader
          text="Accesso protetto in corso..."
          size="large"
          position="page-center"
        />
      }
    >
      {isAuthenticated && !isAccountLoading && children}
    </Suspense>
  );
};
